import type { SearchQueryInput } from '@contracts';
import { useSearchParams } from 'react-router';
import { parseFilters, toSearchQuery, writeFilters, type FilterState } from '../search/state';
import { minuteFloor } from '../lib/time';
import { todayCategories, todayStates } from '@api/today/contracts';
import type { TodayCategory, TodayState } from '@api/today/contracts';

export function useTodayScope() {
  const [params, setParams] = useSearchParams();
  const parsed = parseFilters(params, { kind: 'device' });
  const state = { ...parsed.state, kind: 'device' as const, page: 1 };
  const workState = todayStates.includes(params.get('workState') as TodayState) ? params.get('workState') as TodayState : undefined;
  const category = todayCategories.includes(params.get('category') as TodayCategory) ? params.get('category') as TodayCategory : undefined;
  if (params.has('workState') && !workState) parsed.invalid.push('workState');
  if (params.has('category') && !category) parsed.invalid.push('category');
  const page = /^\d{1,3}$/.test(params.get('workPage') ?? '') ? Math.max(1, Math.min(401, Number(params.get('workPage')))) : 1;
  const update = (next: FilterState) => {
    const out = writeFilters(next, { kind: 'device' });
    if (workState) out.set('workState', workState);
    if (category) out.set('category', category);
    setParams(out);
  };
  const setWork = (key: 'workState' | 'category' | 'workPage', value: string) => {
    const out = new URLSearchParams(params); out.delete('workPage');
    if (value) out.set(key, value); else out.delete(key);
    setParams(out);
  };
  const query = toSearchQuery({ ...state, size: 100, page: 1 }, minuteFloor());
  return { state, query, invalid: parsed.invalid, update, workState, category, page, setWork };
}

/** Derived inventory searches add their own predicates without changing the scope's facet semantics. */
export function inventoryQuery(scope: SearchQueryInput, derived: SearchQueryInput): SearchQueryInput {
  const groups = [...(scope.filters ?? []), ...(derived.filters ?? [])];
  const clauses = groups.flatMap(group => group.mode === 'and' ? group.clauses : []);
  const filters = groups.filter(group => group.mode === 'or');
  // AND(A, B) AND AND(C) is AND(A, B, C). OR groups must stay intact, and each AND chunk stays bounded.
  // This is only for derived searches: facet requests retain their original per-field groups.
  for (let start = 0; start < clauses.length; start += 8) filters.push({ mode: 'and', clauses: clauses.slice(start, start + 8) });
  return { ...derived, q: scope.q, filters };
}
